import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  SqDropdownMenu,
  SqDropdownMenuTrigger,
  SqDropdownMenuContent,
  SqDropdownMenuItem,
} from "@/components/squircle-ui/dropdown";
import { useSearchParams, useParams } from "react-router-dom";
import { useEffect, useState, useCallback, useRef } from "react";
import { fetchOverviewPrompts, createOverviewPrompt } from "@/screens/settings/apiService";
import type { IOverviewPrompt } from "@/screens/settings/types";
import CreateEditDialog from "@/screens/settings/components/CreativeOverview/CreateEditDialog";
import { LuLoaderCircle } from "react-icons/lu";
import { toast } from "sonner";
import { LuCircleCheck } from "react-icons/lu";
import ToastContent from "@/components/common/toast/ToastContent";
import { capitalizeFirstLetter } from "@/screens/utils/Helpers";
import { cn } from "@/lib/utils";
import {
  RiImage2Line,
  RiMovieLine,
  RiInformationLine,
  RiPencilLine,
  RiDeleteBinLine,
  RiAddLine,
  RiCheckLine,
  RiCheckFill,
  RiArrowDownSLine,
  RiArrowUpSLine,
  RiAddLargeLine,
  RiFoldersLine,
  RiFolderOpenLine,
} from "@remixicon/react";
import WaveLoader from "@/components/common/loaders/WaveLoader";
import CreativeGalleryPreview from "@/screens/creatives/components/preview/CreativeGalleryPreview";
import { createLabelV2, getLabelV2ById, editLabelV2, fetchLabelGroupCreatives, type LabelGroup, type LabelGroupCreativeItem } from "../apiService";
import { SqButton } from "@/components/squircle-ui/button";
import { SqTooltip, SqTooltipContent, SqTooltipProvider, SqTooltipTrigger } from "@/components/squircle-ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";

// ─── Types ────────────────────────────────────────────────────────────

interface LabelDescriptionDialogProps {
  onCreate: (labelId: string) => Promise<void>;
  onEdit: (labelId: string, isTitleOnlyChange?: boolean) => Promise<void>;
  report_type?: string;
  onSubmitStart?: () => void;
  onSubmitError?: () => void;
}

type OpenSection = "groups" | "advanced" | null;

/** Source used in "Sources used" chips: title + optional creative_type for media icon */
type SourceUsedItem = { title: string; creative_type?: "video" | "image" | null };

// ─── Segmented Toggle ─────────────────────────────────────────────────

const SegmentedToggle = ({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string; disabled?: boolean; tooltip?: string }[];
}) => (
  <div className="h-[32px] inline-flex items-center p-[2px] rounded-[8px] border border-[#E7E8E9] bg-white">
    {options.map((opt) => {
      const isDisabled = opt.disabled ?? false;
      const button = (
        <button
          key={opt.value}
          type="button"
          disabled={isDisabled}
          onClick={() => {
            if (!isDisabled) onChange(opt.value);
          }}
          className={cn(
            "flex items-center justify-center gap-[6px] h-[28px] min-w-[60px] px-[18px] py-[6px] rounded-[6px] border text-[11px] font-medium leading-[19.157px] tracking-[0.22px] overflow-hidden text-ellipsis transition-colors",
            value === opt.value
              ? "border-[#FFB599] bg-[#FFF4F0] text-[#FF4500]"
              : "border-transparent text-[rgba(2,24,89,0.80)]",
            isDisabled && "opacity-50 cursor-not-allowed",
          )}
        >
          {opt.label}
        </button>
      );

      if (opt.tooltip && isDisabled) {
        return (
          <SqTooltip key={opt.value}>
            <SqTooltipTrigger asChild>{button}</SqTooltipTrigger>
            <SqTooltipContent portal={true} align="start" side="bottom" className="max-w-[260px] text-[11px]">
              {opt.tooltip}
            </SqTooltipContent>
          </SqTooltip>
        );
      }

      return button;
    })}
  </div>
);

// ─── Checkbox Pill ────────────────────────────────────────────────────

const CheckboxPill = ({
  checked,
  onChange,
  label,
  icon,
  disabled,
  tooltip,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  tooltip?: string;
}) => {
  const button = (
    <button
      type="button"
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={cn(
        "h-[32px] flex justify-center items-center gap-2 px-3 py-[6px] pl-2 text-[12px] font-normal transition-colors text-brand-primary-800 squircle squircle-radius-[8px] squircle-smooth-[1] squircle-outline-[1px]",
        checked
          ? "squircle-outline-color-[#FF8F66] !bg-[#FFF9F7]"
          : "squircle-outline-color-border-gray !bg-white",
        disabled && "opacity-50 cursor-not-allowed",
      )}
    >
      <div
        className={cn(
          "h-[14px] w-[14px] shrink-0 rounded-sm squircle squircle-radius-[4px] squircle-smooth-[1]  flex items-center justify-center text-current",
          checked
            ? "squircle-fill-[#FF6A33] squircle-mask text-white"
            : "squircle-fill-border-gray squircle-outline-[1px]",
        )}
      >
        {checked && <RiCheckFill className="h-[85%] w-[85%] text-white" />}
      </div>
      <div className="flex items-center gap-1">
        {icon != null && <span className="shrink-0 flex items-center justify-center">{icon}</span>}
        <span className="mt-[1px] text-[12px] font-normal text-nowrap">{label}</span>
      </div>
    </button>
  );

  if (tooltip && disabled) {
    return (
      <SqTooltip>
        <SqTooltipTrigger asChild>{button}</SqTooltipTrigger>
        <SqTooltipContent portal={true} align="start" side="bottom" className="max-w-[260px] text-[11px]">
          {tooltip}
        </SqTooltipContent>
      </SqTooltip>
    );
  }

  return button;
};

// ─── Main Component ───────────────────────────────────────────────────

export const LabelDescriptionDialogV2 = ({
  onCreate,
  onEdit,
  report_type,
  onSubmitStart,
  onSubmitError,
}: LabelDescriptionDialogProps) => {
  const { accountId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [open, setOpen] = useState(false);
  const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const type = searchParams.get("type");
  const isCreate = !searchParams.get("id");

  // ── Core fields ──
  const [title, setTitle] = useState("");
  const [prompt, setPrompt] = useState("");
  const [initialTitle, setInitialTitle] = useState("");
  const [initialPrompt, setInitialPrompt] = useState("");
  const [id, setId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Media type (checkboxes) ──
  const [videoChecked, setVideoChecked] = useState(true);
  const [imageChecked, setImageChecked] = useState(false);
  const [initialCreativeType, setInitialCreativeType] = useState<string | null>(null);

  const derivedCreativeType: string | null =
    videoChecked && imageChecked ? null : videoChecked ? "video" : imageChecked ? "image" : null;

  // ── Label type ──
  const [labelType, setLabelType] = useState<"unique" | "multiple">("unique");
  const [initialLabelType, setInitialLabelType] = useState<string | null>(null);

  // ── Advanced settings ──
  const [openSection, setOpenSection] = useState<OpenSection>("groups");
  const [labelSource, setLabelSource] = useState<"auto" | "creative" | "ad_metadata">("auto");
  const [overviewSelectionType, setOverviewSelectionType] = useState<"auto" | "manual">("auto");
  const [metadataSelectionType, setMetadataSelectionType] = useState<"auto" | "manual">("auto");
  const [selectedMetadata, setSelectedMetadata] = useState<string[]>([]);
  const [selectedOverviewIds, setSelectedOverviewIds] = useState<string[]>([]);
  const [overviewPrompts, setOverviewPrompts] = useState<IOverviewPrompt[]>([]);
  const [isCreateOverviewOpen, setIsCreateOverviewOpen] = useState(false);

  // ── Initial label sources for change detection ──
  const [initialLabelSources, setInitialLabelSources] = useState<Record<string, unknown> | null>(null);

  // ── Edit: label groups ──
  const [labelGroups, setLabelGroups] = useState<LabelGroup[]>([]);
  const [syncStatus, setSyncStatus] = useState<string>("");
  const [rememberLabelGroups, setRememberLabelGroups] = useState(false);

  // Group mutations tracking
  const [groupRenames, setGroupRenames] = useState<Array<{ id: string; name: string }>>([]);
  const [groupDeletes, setGroupDeletes] = useState<string[]>([]);
  const [groupAdds, setGroupAdds] = useState<Array<{ id?: string; name: string }>>([]);
  const [renamingGroupId, setRenamingGroupId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");

  // ── Gallery preview ──
  type GalleryCreativeState = LabelGroupCreativeItem;
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryCreatives, setGalleryCreatives] = useState<GalleryCreativeState[]>([]);
  const [galleryGroupName, setGalleryGroupName] = useState("");
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryTotal, setGalleryTotal] = useState(0);
  const galleryNextPageRef = useRef(1);
  const galleryGroupIdRef = useRef<string | null>(null);
  const galleryTotalRef = useRef(0);

  const GALLERY_PAGE_SIZE = 20;

  useEffect(() => {
    galleryTotalRef.current = galleryTotal;
  }, [galleryTotal]);

  // ── Polling for pending status ──
  const POLLING_INTERVAL = 3000;

  const pollLabelConfig = useCallback(async () => {
    if (!accountId || !id) return;
    try {
      const data = await getLabelV2ById(accountId, id);
      setSyncStatus(data.sync_status);
      setLabelGroups(data.label_groups);
    } catch {
      // ignore polling errors
    }
  }, [accountId, id]);

  useEffect(() => {
    if (!id || !accountId || isCreate) return;
    if (syncStatus !== "pending") return;

    const interval = setInterval(() => {
      void pollLabelConfig();
    }, POLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [id, accountId, isCreate, syncStatus, pollLabelConfig]);

  // ── Refresh generated groups when a label is changed in the gallery ──
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ labelConfigId: string }>).detail;
      if (detail?.labelConfigId === id) {
        void pollLabelConfig();
      }
    };
    window.addEventListener("labelUpdated", handler);
    return () => window.removeEventListener("labelUpdated", handler);
  }, [id, pollLabelConfig]);

  // ── Fetch overview prompts ──
  useEffect(() => {
    if (!accountId || !open) return;
    const load = async () => {
      try {
        const res = await fetchOverviewPrompts(accountId);
        setOverviewPrompts(res.data);
      } catch {
        // ignore
      }
    };
    void load();
  }, [accountId, open]);

  // ── Remove selected overviews that no longer match media type ──
  useEffect(() => {
    if (labelSource !== "creative") return;
    const validTypes: (string | null)[] = [];
    if (videoChecked) validTypes.push("video");
    if (imageChecked) validTypes.push("image");

    setSelectedOverviewIds((prev) =>
      prev.filter((oid) => {
        const p = overviewPrompts.find((op) => op.id === oid);
        return p && validTypes.includes(p.creative_type);
      }),
    );
  }, [videoChecked, imageChecked, labelSource, overviewPrompts]);

  // ── Open / close / id tracking ──
  useEffect(() => {
    setOpen(!!searchParams.get("type"));
    setId(searchParams.get("id") ?? "");
  }, [searchParams]);

  // ── Fetch label config on edit ──
  useEffect(() => {
    const fetchData = async () => {
      if (!id || !accountId || !type || isCreate) return;
      setIsLoading(true);
      try {
        const data = await getLabelV2ById(accountId, id);
        setTitle(data.title);
        setPrompt(data.prompt);
        setInitialTitle(data.title);
        setInitialPrompt(data.prompt);
        setInitialCreativeType(data.creative_type);
        setInitialLabelType(data.label_type);
        setSyncStatus(data.sync_status);
        setLabelGroups(data.label_groups);
        setInitialLabelSources(data.label_sources);

        // Media type
        if (data.creative_type === "video") {
          setVideoChecked(true);
          setImageChecked(false);
        } else if (data.creative_type === "image") {
          setVideoChecked(false);
          setImageChecked(true);
        } else {
          setVideoChecked(true);
          setImageChecked(true);
        }

        setLabelType(data.label_type);

        // Reset group mutations so Update stays disabled until user makes changes
        setGroupRenames([]);
        setGroupDeletes([]);
        setGroupAdds([]);

        // Label sources
        const sources = (data.label_sources as Record<string, unknown>)?.sources as Record<string, unknown> | undefined;
        if (sources) {
          setLabelSource((sources.label_source as "auto" | "creative" | "ad_metadata") || "auto");
          setOverviewSelectionType((sources.overview_selection_type as "auto" | "manual") || "auto");
          setMetadataSelectionType((sources.metadata_selection_type as "auto" | "manual") || "auto");
          setSelectedMetadata((sources.metadata as string[]) || []);
          setSelectedOverviewIds((sources.overview_ids as string[]) || []);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    void fetchData();
  }, [id, accountId, type, isCreate]);

  // ── Reset ──
  const resetStates = () => {
    setTitle("");
    setPrompt("");
    setInitialTitle("");
    setInitialPrompt("");
    setInitialCreativeType(null);
    setInitialLabelType(null);
    setInitialLabelSources(null);
    setVideoChecked(true);
    setImageChecked(false);
    setLabelType("unique");
    setOpenSection("groups");
    setLabelSource("auto");
    setOverviewSelectionType("auto");
    setMetadataSelectionType("auto");
    setSelectedMetadata([]);
    setSelectedOverviewIds([]);
    setLabelGroups([]);
    setSyncStatus("");
    setRememberLabelGroups(false);
    setGroupRenames([]);
    setGroupDeletes([]);
    setGroupAdds([]);
    setRenamingGroupId(null);
    setIsCreateOverviewOpen(false);
  };

  const onClose = () => {
    setTimeout(() => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.delete("type");
        params.delete("id");
        params.delete("apply");
        params.delete("customPreset");
        return Object.fromEntries(params.entries());
      });
      resetStates();
    }, 100);
  };

  const toggleSection = (section: OpenSection) => {
    setOpenSection((prev) => (prev === section ? null : section));
  };

  const handleCreateOverview = async (
    overviewTitle: string,
    description: string,
    creativeTypeFromDialog?: "video" | "image",
  ) => {
    if (!accountId) return;
    const creativeType =
      creativeTypeFromDialog ??
      (derivedCreativeType === "image" || derivedCreativeType === "video" ? derivedCreativeType : "video");
    try {
      const response = await createOverviewPrompt(accountId, {
        title: overviewTitle,
        prompt: description,
        creative_type: creativeType,
      });
      setOverviewPrompts((prev) => [...prev, response.data]);
      setOverviewSelectionType("manual");
      toast.message(
        <ToastContent
          icon={LuCircleCheck}
          headingPrefix="Overview created"
          subheadingSuffix="The overview has been created."
          isError={false}
        />,
      );
    } catch (err) {
      console.error("Error creating overview prompt:", err);
      toast.error("Failed to create overview. Please try again.");
    }
  };

  // ── Submit ──
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accountId) return;

    if (isCreate) {
      const savedType = type;
      onClose();
      onSubmitStart?.();
      createLabelV2(accountId, {
        title,
        prompt,
        creative_type: derivedCreativeType,
        label_type: labelType,
        label_sources: {
          label_source: labelSource,
          overview_selection_type: labelSource === "creative" ? overviewSelectionType : undefined,
          metadata_selection_type: labelSource === "ad_metadata" ? metadataSelectionType : undefined,
          overview_ids: labelSource === "creative" && overviewSelectionType === "manual" ? selectedOverviewIds : [],
          metadata: labelSource === "ad_metadata" && metadataSelectionType === "manual" ? selectedMetadata : [],
        },
        ...(report_type ? { report_type } : {}),
      })
        .then((response) => onCreate(response.id))
        .catch((error) => {
          console.error(`Error creating ${savedType}:`, error);
          onSubmitError?.();
        });
      return;
    }

    // Update: wait for API, then close
    setIsSubmitting(true);
    try {
      const hasGroupMutations = groupRenames.length > 0 || groupDeletes.length > 0 || groupAdds.length > 0;
      const promptChanged = prompt.trim() !== initialPrompt.trim();
      const creativeTypeChanged = derivedCreativeType !== initialCreativeType;
      const labelTypeChanged = labelType !== initialLabelType;

      const currentLabelSources = {
        label_source: labelSource,
        overview_selection_type: labelSource === "creative" ? overviewSelectionType : undefined,
        metadata_selection_type: labelSource === "ad_metadata" ? metadataSelectionType : undefined,
        overview_ids: labelSource === "creative" && overviewSelectionType === "manual" ? selectedOverviewIds : [],
        metadata: labelSource === "ad_metadata" && metadataSelectionType === "manual" ? selectedMetadata : [],
      };
      const prevSources = (initialLabelSources as Record<string, unknown>)?.sources as Record<string, unknown> | undefined;
      const labelSourcesChanged =
        JSON.stringify(currentLabelSources) !==
        JSON.stringify({
          label_source: (prevSources?.label_source as string) || "auto",
          overview_selection_type: (prevSources?.overview_selection_type as string) || "auto",
          metadata_selection_type: (prevSources?.metadata_selection_type as string) || "auto",
          overview_ids: (prevSources?.overview_ids as string[]) || [],
          metadata: (prevSources?.metadata as string[]) || [],
        });

      const configChanged = promptChanged || creativeTypeChanged || labelTypeChanged || labelSourcesChanged;
      const isTitleOnlyChange = !configChanged && !hasGroupMutations;
      const titleChanged = title.trim() !== (initialTitle ?? "").trim();

      const payload: Record<string, unknown> = {};
      if (titleChanged) payload.title = title;
      if (promptChanged) payload.prompt = prompt;
      if (creativeTypeChanged) payload.creative_type = derivedCreativeType;
      if (labelTypeChanged) payload.label_type = labelType;
      if (labelSourcesChanged) payload.label_sources = currentLabelSources;
      if (configChanged) payload.remember_label_groups = rememberLabelGroups;
      if (hasGroupMutations) {
        const labelGroupsPayload: Record<string, unknown> = {};
        if (groupRenames.length > 0) labelGroupsPayload.rename = groupRenames;
        if (groupDeletes.length > 0) labelGroupsPayload.delete = groupDeletes;
        if (groupAdds.length > 0)
          labelGroupsPayload.add = groupAdds.map(({ name }) => ({ name }));
        payload.label_groups = labelGroupsPayload;
      }

      await editLabelV2(accountId, id, payload as Parameters<typeof editLabelV2>[2]);
      await onEdit(id, isTitleOnlyChange);

      toast.message(
        <ToastContent
          icon={LuCircleCheck}
          headingPrefix={`${capitalizeFirstLetter(type ?? "")} updated`}
          subheadingSuffix={`${capitalizeFirstLetter(type ?? "")} has been updated successfully`}
          isError={false}
        />,
      );
      onClose();
    } catch (error) {
      console.error(`Error updating ${type}:`, error);
      onSubmitError?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── See All handler: use preview when < 4 creatives, else fetch page 1 from BE (scroll loads more) ──
  const handleSeeAll = async (groupId: string, groupName: string, group?: LabelGroup) => {
    if (!accountId) return;
    setGalleryGroupName(groupName);
    const previews = group?.creatives_preview ?? [];
    const totalCreatives = group?.total_creatives ?? 0;
    const usePreviewOnly = totalCreatives < 4 && previews.length > 0;

    if (usePreviewOnly) {
      setGalleryCreatives(previews);
      setGalleryTotal(0);
      galleryNextPageRef.current = 1;
      galleryGroupIdRef.current = null;
      setGalleryLoading(false);
      setGalleryOpen(true);
      return;
    }

    setGalleryCreatives([]);
    setGalleryTotal(0);
    galleryNextPageRef.current = 1;
    setGalleryLoading(true);
    setGalleryOpen(true);
    try {
      const resp = await fetchLabelGroupCreatives(accountId, groupId, 1, GALLERY_PAGE_SIZE);
      setGalleryCreatives(resp.creatives);
      setGalleryTotal(resp.total_creatives);
      galleryNextPageRef.current = 2;
      galleryGroupIdRef.current = groupId;
    } catch (err) {
      console.error("Error fetching group creatives:", err);
      setGalleryOpen(false);
    } finally {
      setGalleryLoading(false);
    }
  };

  const handleGalleryLoadMore = useCallback(async () => {
    const groupId = galleryGroupIdRef.current;
    if (!accountId || !groupId) return;
    const nextPage = galleryNextPageRef.current;
    const total = galleryTotalRef.current || 0;
    const totalPages = Math.ceil(total / GALLERY_PAGE_SIZE) || 1;
    if (nextPage > totalPages) return;
    try {
      const resp = await fetchLabelGroupCreatives(accountId, groupId, nextPage, GALLERY_PAGE_SIZE);
      setGalleryCreatives((prev) => [...prev, ...resp.creatives]);
      galleryNextPageRef.current = nextPage + 1;
    }
    catch (err) {
      console.error("Error fetching group creatives:", err);
    }
  }, [accountId]);

  const handleGalleryOpenChange = useCallback((open: boolean) => {
    setGalleryOpen(open);
    if (!open) {
      setGalleryTotal(0);
      galleryTotalRef.current = 0;
      galleryNextPageRef.current = 1;
      galleryGroupIdRef.current = null;
    }
  }, []);

  // ── Rename confirm ──
  const confirmRename = (groupId: string) => {
    if (!renameValue.trim()) {
      setRenamingGroupId(null);
      return;
    }
    const trimmed = renameValue.trim();
    if (groupId.startsWith("temp-")) {
      setGroupAdds((prev) =>

        prev.map((a) => (a.id === groupId ? { ...a, name: trimmed } : a)),
      );

    } else {
      setGroupRenames((prev) => {
        const existing = prev.find((r) => r.id === groupId);
        if (existing) return prev.map((r) => (r.id === groupId ? { ...r, name: trimmed } : r));
        return [...prev, { id: groupId, name: trimmed }];
      });
    }
    setLabelGroups((prev) => prev.map((g) => (g.id === groupId ? { ...g, name: trimmed } : g)));
    setRenamingGroupId(null);
  };

  // ── Add group (creates "New group" and opens rename) ──
  const handleAddGroup = () => {
    const newId = `temp-${Date.now()}`;
    const defaultName = "New group";
    setGroupAdds((prev) => [{ id: newId, name: defaultName }, ...prev]);
    setLabelGroups((prev) => [
      {
        id: newId,
        name: defaultName,
        description: "",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        total_creatives: 0,
        creatives_preview: [],
      },
      ...prev,
    ]);
    setRenamingGroupId(newId);
    setRenameValue(defaultName);
  };

  // ── Delete group ──
  const handleDeleteGroup = (groupId: string) => {
    if (groupId.startsWith("temp-")) {
      setGroupAdds((prev) => prev.filter((a) => a.id !== groupId));
    } else {
      setGroupDeletes((prev) => [...prev, groupId]);
    }
    setLabelGroups((prev) => prev.filter((g) => g.id !== groupId));
  };

  const isFormValid = () => {
    return title.trim() !== "" && prompt.trim() !== "" && (videoChecked || imageChecked);
  };

  const filteredOverviews = overviewPrompts.filter((p) => {
    if (videoChecked && imageChecked) return true;
    if (videoChecked) return p.creative_type === "video";
    if (imageChecked) return p.creative_type === "image";
    return false;
  });

  const selectedOverviewPrompts = overviewPrompts.filter((p) => selectedOverviewIds.includes(p.id));
  const hasVideoOverviewSelected = selectedOverviewPrompts.some((p) => p.creative_type === "video");
  const hasImageOverviewSelected = selectedOverviewPrompts.some((p) => p.creative_type === "image");
  const isCreativeOverviewSelectionIncomplete =
    labelSource === "creative" &&
    overviewSelectionType === "manual" &&
    (videoChecked && imageChecked
      ? !(hasVideoOverviewSelected && hasImageOverviewSelected)
      : selectedOverviewIds.length === 0);

  const creativeOverviewTooltipMessage = ((): string => {
    if (!isCreativeOverviewSelectionIncomplete) return "";
    if (videoChecked && imageChecked) {
      if (!hasVideoOverviewSelected && !hasImageOverviewSelected)
        return "Almost there, Choose an image overview and a video overview from creative overview, or set Creative overview to Auto.";
      if (!hasImageOverviewSelected)
        return "Almost there, Choose an image overview from creative overview, or set Creative overview to Auto.";
      if (!hasVideoOverviewSelected)
        return "Almost there, Choose a video overview from creative overview, or set Creative overview to Auto.";
    }
    return "Almost there, Choose a creative overview, or set Creative overview to Auto.";
  })();

  const isAdMetadataSelectionIncomplete =
    labelSource === "ad_metadata" &&
    metadataSelectionType === "manual" &&
    selectedMetadata.length === 0;

  const adMetadataTooltipMessage = isAdMetadataSelectionIncomplete
    ? "Almost there, Choose at least one ad metadata field from Ad metadata fields, or set Ad metadata fields to Auto."
    : "";

  const hasGroupMutations =
    groupRenames.length > 0 || groupDeletes.length > 0 || groupAdds.length > 0;
  const prevLabelSources = (initialLabelSources as Record<string, unknown>)?.sources as
    | Record<string, unknown>
    | undefined;
  // Use same shape and defaults as prev so JSON.stringify comparison is accurate (no undefined vs "auto" mismatch)
  const currentLabelSourcesForCompare = {
    label_source: labelSource,
    overview_selection_type: labelSource === "creative" ? overviewSelectionType : "auto",
    metadata_selection_type: labelSource === "ad_metadata" ? metadataSelectionType : "auto",
    overview_ids:
      labelSource === "creative" && overviewSelectionType === "manual" ? selectedOverviewIds : [],
    metadata:
      labelSource === "ad_metadata" && metadataSelectionType === "manual" ? selectedMetadata : [],
  };
  // Mirror current's conditional logic so we compare like-for-like (e.g. overview_ids only matter when label_source is "creative" + manual)
  const prevSource = (prevLabelSources?.label_source as string) || "auto";
  const prevOverviewType = (prevLabelSources?.overview_selection_type as string) || "auto";
  const prevMetadataType = (prevLabelSources?.metadata_selection_type as string) || "auto";
  const prevLabelSourcesForCompare = {
    label_source: prevSource,
    overview_selection_type: prevSource === "creative" ? prevOverviewType : "auto",
    metadata_selection_type: prevSource === "ad_metadata" ? prevMetadataType : "auto",
    overview_ids:
      prevSource === "creative" && prevOverviewType === "manual"
        ? ((prevLabelSources?.overview_ids as string[]) || [])
        : [],
    metadata:
      prevSource === "ad_metadata" && prevMetadataType === "manual"
        ? ((prevLabelSources?.metadata as string[]) || [])
        : [],
  };
  const labelSourcesChanged =
    initialLabelSources != null &&
    JSON.stringify(currentLabelSourcesForCompare) !== JSON.stringify(prevLabelSourcesForCompare);

  // Only treat as changed when we have a loaded baseline (edit mode); otherwise initial* are null and we'd falsely report changes
  const hasEditFormChanges =
    isCreate ||
    title.trim() !== (initialTitle ?? "").trim() ||
    prompt.trim() !== (initialPrompt ?? "").trim() ||
    (initialCreativeType != null && derivedCreativeType !== initialCreativeType) ||
    (initialLabelType != null && labelType !== initialLabelType) ||
    labelSourcesChanged ||
    hasGroupMutations;

  const subtitle = isCreate
    ? "Define a label to automatically group ads using info from their image or video."
    : "Update the prompt or settings to rebuild all groups, or just tweak the groups yourself.";
  const buttonText = isCreate ? "Create" : "Update";

  const submitButtonTooltipMessage = creativeOverviewTooltipMessage
    || adMetadataTooltipMessage
    || (!hasEditFormChanges ? "Make a change to update the label." : "")
    || (!isFormValid() ? "Enter a title and prompt, and select at least one media type." : "");

  // Advanced settings edited count (0, 1, or 2): label source not "Auto" = 1, label type not "Unique" = 1
  const advancedEditedCount =
    (labelSource !== "auto" ? 1 : 0) + (labelType !== "unique" ? 1 : 0);

  // ── Sources used pills (title + creative_type for media icon) ──
  const sourcesUsed: SourceUsedItem[] = [];
  if (initialLabelSources) {
    const sources = (initialLabelSources)?.sources as Record<string, unknown> | undefined;
    if (sources) {
      const ovIds = (sources.overview_ids as string[]) || [];
      ovIds.forEach((oid) => {
        const p = overviewPrompts.find((op) => op.id === oid);
        if (p) sourcesUsed.push({
          title: p.title,
          creative_type: p.creative_type === "video" || p.creative_type === "image" ? p.creative_type : undefined,
        });
      });
    }
  }

  const showGeneratedGroups = !isCreate && labelGroups.length > 0 || (!isCreate && syncStatus === "pending");

  return (
    <>
      <Dialog
        open={open && !isCreateOverviewOpen && !unsavedChangesDialogOpen}
        onOpenChange={(o) => {
          if (!o && isCreateOverviewOpen) return;
          if (!o && !isCreate && hasEditFormChanges) {
            setUnsavedChangesDialogOpen(true);
            return;
          }
          setOpen(o);
          if (!o) onClose();
        }}
      >
        <DialogContent className="max-w-[716px] overflow-hidden p-0 gap-0 !rounded-[16px] top-0 translate-y-0 mt-[18vh]" hideCloseButton>
          <DialogHeader className="pt-4 px-[18px] space-y-[2px]">
            <DialogTitle className="overflow-hidden text-brand-primary-new text-ellipsis text-[14px] font-medium not-italic leading-[18px]">
              {isCreate ? "Create" : "Edit"} {type}
            </DialogTitle>
            <p className="text-brand-primary-600 text-[14px] leading-[18px]">{subtitle}</p>
          </DialogHeader>

          <SqTooltipProvider delayDuration={500}>
            {isLoading ? (
              <div className="flex justify-center items-center h-full p-[50px]">
                <LuLoaderCircle className="h-4 w-4 animate-spin" />
              </div>
            ) : (
              <form ref={formRef} onSubmit={(e) => void handleSubmit(e)} className="w-full overflow-hidden">
                {/* ── Title & Prompt ── */}
                <div className="pr-[14px] pl-[13px] pt-4">
                  <Input
                    placeholder={`Enter ${type} title`}
                    className="h-[32px] w-full px-[14px] py-[6px] text-[12px] font-medium placeholder:font-normal placeholder:text-brand-primary-400 text-brand-primary-800 rounded-[8px] border-[1px] border-[#D0D5DD99] bg-[#FBFBFB] shadow-none"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </div>
                <div className="pr-[14px] pl-[13px] mt-[10px]">
                  <Textarea
                    placeholder="Tell us what you want to look at in creatives, how you want to group them, and what you want the output to look like. You can also list the exact groups you want."
                    className="h-[154px] px-[14px] pt-2 pb-[6px] resize-none placeholder:text-brand-primary-400 focus-visible:ring-0 text-brand-primary-800 text-[12px] font-normal rounded-[8px] border-[1px] border-[#D0D5DD99] bg-[#FBFBFB] shadow-none"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                  />
                </div>

                {/* ── Generated Groups (Edit mode) ── */}
                {showGeneratedGroups && (
                  <div className="ml-[13px] mr-[14px] mt-2 border border-[#D0D5DD99] rounded-[8px] overflow-hidden">
                    {/* Header */}
                    <button
                      type="button"
                      className="w-full h-[40px] flex items-center justify-between px-[12px] bg-white hover:bg-[#FBFBFB] transition-colors"
                      onClick={() => toggleSection("groups")}
                    >
                      <div className="flex items-center gap-[8px]">
                        <RiFoldersLine size={18} className="text-brand-primary-400 shrink-0" />
                        <SqTooltip>
                          <SqTooltipTrigger asChild>
                            <span className="text-[12px] font-medium text-brand-primary-800 cursor-default">
                              Generated groups ({labelGroups.length})
                              <span className="ml-[2px] text-[#FF6A33] font-medium text-[16px] not-italic">*</span>
                            </span>
                          </SqTooltipTrigger>
                          <SqTooltipContent
                            portal={true}
                            align="start"
                            side="bottom"
                            className="max-w-[320px] text-[11px]"
                          >
                            If your selected date range falls within the last 3 months, groups are generated for the entire
                            last 3 months. For any range that extends beyond that or is a historical date range, groups are
                            generated for your exact selection only.
                          </SqTooltipContent>
                        </SqTooltip>
                        <RiArrowDownSLine
                          size={18}
                          className={cn(
                            "text-brand-primary-400 shrink-0 transition-transform duration-200",
                            openSection === "groups" ? "rotate-180" : ""
                          )}
                        />
                      </div>
                      <label
                        className="flex items-center gap-2 text-[12px] text-brand-primary-800 cursor-pointer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={rememberLabelGroups}
                          onCheckedChange={(checked) => setRememberLabelGroups(checked as boolean)}
                        />
                        <span className="mt-[2px]">
                          Remember current group names
                        </span>
                        <SqTooltip>
                          <SqTooltipTrigger type="button">
                            <RiInformationLine size={14} className="text-[#CCD1DE] shrink-0" />
                          </SqTooltipTrigger>
                          <SqTooltipContent portal={true} align="start" side="bottom" className="max-w-[240px] text-[11px]">
                            Turn this on to keep your current group names when you update the label prompt or settings. Otherwise, updating may create brand-new groups.
                          </SqTooltipContent>
                        </SqTooltip>
                      </label>
                    </button>

                    {/* Content */}
                    {openSection === "groups" && (
                      <div className="border-t border-[#D0D5DD99] px-[14px] py-[8px]">
                        {syncStatus === "pending" ? (
                          <div className="flex justify-center items-center py-[30px]">
                            <WaveLoader className="w-[100px]" />
                          </div>
                        ) : (
                          <div className="flex flex-col gap-[10px]">
                            <div className="max-h-[200px] overflow-y-auto grid grid-cols-3 gap-y-[6px] gap-x-[12px]">
                              {labelGroups.map((group) => (
                                <div key={group.id} className="group h-[78px] rounded-[8px] overflow-hidden bg-[#F0F1F5] squircle squircle-radius-[8px] squircle-smooth-[1] squircle-fill-[#F0F1F5]">
                                  {/* Thumbnail row: empty state when no ads; else fixed 1/4 width per slot. If <4 images, whole row opens gallery; if 4 images, "See all" overlay on 4th only. */}
                                  {(() => {
                                    const previews = group.creatives_preview || [];
                                    const totalCreatives = group.total_creatives || 0;
                                    const hasNoAds = totalCreatives === 0;
                                    const hasFourOrMoreImages = previews.length >= 4;
                                    const showSeeAllOverlay = hasFourOrMoreImages && totalCreatives > 0;
                                    const openGalleryOnRowClick = !hasFourOrMoreImages && totalCreatives > 0;

                                    if (hasNoAds) {
                                      return (
                                        <div className="relative h-[50px] flex items-center justify-center bg-[#F0F1F5] text-brand-primary-400">
                                          <div className="flex items-center justify-center gap-[6px]">
                                            <RiFolderOpenLine size={14} className="shrink-0 text-brand-primary-400" />
                                            <span className="text-[12px] text-brand-primary-400 font-medium">Empty</span>
                                          </div>
                                        </div>
                                      );
                                    }

                                    return (
                                      <div
                                        role={openGalleryOnRowClick ? "button" : undefined}
                                        tabIndex={openGalleryOnRowClick ? 0 : undefined}
                                        onClick={openGalleryOnRowClick ? () => void handleSeeAll(group.id, group.name, group) : undefined}
                                        onKeyDown={openGalleryOnRowClick ? (e) => { if (e.key === "Enter") void handleSeeAll(group.id, group.name, group); } : undefined}
                                        className={`relative h-[50px] grid grid-cols-4 ${openGalleryOnRowClick ? "cursor-pointer" : ""}`}
                                      >
                                        {[0, 1, 2, 3].map((i) => {
                                          const cp = previews[i];
                                          const isFourthCell = i === 3;
                                          return (
                                            <div
                                              key={i}
                                              className="relative h-full w-full overflow-hidden bg-[#F0F1F5]"
                                            >
                                              {cp?.thumbnail_url ? (
                                                <img
                                                  src={cp.thumbnail_url}
                                                  alt=""
                                                  className="w-full h-full object-cover"
                                                />
                                              ) : null}
                                              {isFourthCell && showSeeAllOverlay && (
                                                <button
                                                  type="button"
                                                  className="absolute inset-0 flex flex-col items-center justify-center bg-[rgba(17,17,17,0.60)] text-white cursor-pointer border-0 p-0"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    void handleSeeAll(group.id, group.name, group);
                                                  }}
                                                >
                                                  <span className="text-[11px] font-normal">See all</span>
                                                  <span className="text-[11px] font-medium">{totalCreatives}</span>
                                                </button>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  })()}

                                  {/* Name row: 24px, same bg, text truncate, icon always visible */}
                                  <div className="flex w-full h-[28px] px-[6px] py-[6px] pl-[8px] justify-between items-center bg-[#F0F1F5] min-w-0">
                                    {renamingGroupId === group.id ? (
                                      <div className="flex items-center gap-1 flex-1 min-w-0">
                                        <Input
                                          value={renameValue}
                                          onChange={(e) => setRenameValue(e.target.value)}
                                          onFocus={(e) => e.target.select()}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                              e.preventDefault();
                                              confirmRename(group.id);
                                            }
                                            if (e.key === "Escape") setRenamingGroupId(null);
                                          }}
                                          autoFocus
                                          className="h-[24px] text-[12px] text-brand-primary-800 font-normal bg-transparent border-0 shadow-none rounded-none p-0 min-w-0 flex-1 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-brand-primary-400"
                                        />
                                        <button
                                          type="button"
                                          onClick={() => confirmRename(group.id)}
                                          className="shrink-0"
                                        >
                                          <RiCheckLine size={14} className="text-brand-orange" />
                                        </button>
                                      </div>
                                    ) : (
                                      <>
                                        <span className="text-[12px] text-brand-primary-800 font-normal truncate flex-1 min-w-0 overflow-hidden text-ellipsis">
                                          {group.name}
                                        </span>
                                        <SqDropdownMenu>
                                          <SqDropdownMenuTrigger
                                            asChild
                                            hasOutline={false}
                                            className="!p-0 !h-auto !min-w-0 !shadow-none before:!hidden !squircle-fill-[#00000000] hover:!squircle-fill-[#00000000]"
                                          >
                                            <button
                                              type="button"
                                              className="inline-flex cursor-pointer border-0 bg-transparent p-0"
                                              aria-label="Group actions"
                                            >
                                              <RiPencilLine size={14} className="text-brand-primary-400 shrink-0" />
                                            </button>
                                          </SqDropdownMenuTrigger>
                                          <SqDropdownMenuContent
                                            portal
                                            align="start"
                                            side="bottom"
                                            radius={12}
                                            className="p-1 min-w-[120px]"
                                          >
                                            <SqDropdownMenuItem
                                              onSelect={() => {
                                                setRenamingGroupId(group.id);
                                                setRenameValue(group.name);
                                              }}
                                              className="flex items-center gap-[6px] text-brand-primary-800 text-[12px]"
                                            >
                                              <RiPencilLine size={14} className="text-brand-primary-400 shrink-0" />
                                              <span className="h-[14px]">Rename</span>
                                            </SqDropdownMenuItem>
                                            <SqTooltip >
                                              <SqTooltipTrigger asChild>
                                                <SqDropdownMenuItem
                                                  onSelect={() => handleDeleteGroup(group.id)}
                                                  bgColor="#FFF7F7"
                                                  hoverBgColor="#FFEBEB"
                                                  className="flex items-center gap-[6px] text-[#FF0000] hover:text-[#FF0000] text-[12px]"
                                                >
                                                  <RiDeleteBinLine size={14} className="text-[#FF0000] shrink-0" />
                                                  <span className="h-[14px]">Delete</span>
                                                </SqDropdownMenuItem>
                                              </SqTooltipTrigger>
                                              <SqTooltipContent portal={true} side="right" align="start" alignOffset={10} className="max-w-[260px] text-[11px]">
                                                Creatives from a deleted group will be reassigned to the remaining groups when you update the label.
                                              </SqTooltipContent>
                                            </SqTooltip>
                                          </SqDropdownMenuContent>
                                        </SqDropdownMenu>
                                      </>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>

                            <div className="flex items-center justify-between gap-4">
                              {/* Add Group */}
                              <div className="flex items-center gap-2">
                                <SqButton
                                  variant="outline"
                                  size="sm"
                                  type="button"
                                  onClick={handleAddGroup}
                                  className="font-medium"
                                >
                                  <RiAddLine size={14} className="text-brand-primary-400" /> <span className="mt-[2px]">Add Group</span>
                                </SqButton>
                                <SqTooltip>
                                  <SqTooltipTrigger asChild>
                                    <span className="inline-flex" role="button" tabIndex={0}>
                                      <RiInformationLine size={14} className="text-[#CCD1DE]" />
                                    </span>
                                  </SqTooltipTrigger>
                                  <SqTooltipContent portal={true} side="bottom" className="max-w-[280px] text-[11px]">
                                    When you create new groups they will start empty. After you update the label, we'll automatically add the matching creatives.
                                  </SqTooltipContent>
                                </SqTooltip>
                              </div>

                              {/* Sources used */}
                              {sourcesUsed.length > 0 && (
                                <div className="min-w-0 flex items-center gap-[6px]">
                                  <span className="text-[12px] text-brand-primary-400 shrink-0 flex-none">
                                    Sources used:
                                  </span>
                                  <div className="overflow-x-auto min-w-0 flex-1">
                                    <div className="flex items-center gap-[6px] flex-nowrap">
                                      {sourcesUsed.map((item, i) => (
                                        <span
                                          key={i}
                                          className="flex items-center justify-center h-[24px] px-[12px] py-[6px] gap-1 text-[11px] font-medium text-brand-orange !bg-[#FFF9F7] rounded-[6px] squircle squircle-radius-[6px] squircle-smooth-[1] squircle-outline-[1px] squircle-outline-color-[#FFDACC] shrink-0"
                                        >
                                          {item.creative_type === "video" ? (
                                            <RiMovieLine size={14} className="shrink-0 text-brand-orange/60" />
                                          ) : item.creative_type === "image" ? (
                                            <RiImage2Line size={14} className="shrink-0 text-brand-orange/60" />
                                          ) : null}
                                          <span className="mt-[1px]">{item.title}</span>
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Bottom Bar ── */}
                <div className="pr-[14px] pl-[13px] py-4 mt-2 flex items-center justify-between gap-4 border-t border-[#D0D5DD99]">
                  <div className="flex items-center gap-[6px]">
                    {/* Media type label */}
                    <span className="text-[12px] text-brand-primary-800 font-medium">Media type:</span>

                    {/* Video checkbox */}
                    <CheckboxPill
                      checked={videoChecked}
                      onChange={setVideoChecked}
                      label="Videos"
                      disabled={labelType === "multiple" && imageChecked}
                      tooltip={
                        labelType === "multiple" && imageChecked
                          ? 'Multi tagging is limited to one asset type. Switch to Unique to tag across Image and Video.'
                          : undefined
                      }

                    />

                    {/* Image checkbox */}
                    <CheckboxPill
                      checked={imageChecked}
                      onChange={setImageChecked}
                      label="Images"
                      disabled={labelType === "multiple" && videoChecked}
                      tooltip={
                        labelType === "multiple" && videoChecked
                          ? 'Multi tagging is limited to one asset type. Switch to Unique to tag across Image and Video.'
                          : undefined
                      }

                    />
                  </div>

                  <div className="flex items-center gap-[8px]">
                    {/* Advanced settings toggle */}
                    <button
                      type="button"
                      className="flex justify-end items-center gap-[8px] h-[32px] px-[4px] py-[6px] pl-[12px] text-[12px] text-brand-primary-800 font-medium"
                      onClick={() => toggleSection("advanced")}
                    >
                      {openSection !== "advanced" && advancedEditedCount > 0 && (
                        <div
                          className="flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[var(--Primary-600,#FF6A33)]"
                          aria-label={`${advancedEditedCount} advanced setting${advancedEditedCount === 1 ? "" : "s"} edited`}
                        >
                          <span className="inline-flex items-center justify-center text-[11px] leading-none text-white font-medium">{advancedEditedCount}</span>
                        </div>
                      )}
                      Advanced settings
                      {openSection === "advanced" ? (
                        <RiArrowUpSLine className="h-4 w-4 text-brand-primary-400 shrink-0" />
                      ) : (
                        <RiArrowDownSLine className="h-4 w-4 text-brand-primary-400 shrink-0" />
                      )}
                    </button>

                    {/* Submit — only submit button in the form */}
                    {submitButtonTooltipMessage ? (
                      <SqTooltip>
                        <SqTooltipTrigger asChild>
                          <span className="inline-flex">
                            <SqButton
                              type="submit"
                              variant="default"
                              size="md"
                              className="font-medium"
                              disabled={
                                !isFormValid() ||
                                isSubmitting ||
                                isCreativeOverviewSelectionIncomplete ||
                                isAdMetadataSelectionIncomplete ||
                                !hasEditFormChanges
                              }
                            >
                              {isSubmitting ? (
                                <LuLoaderCircle className="h-4 w-4 animate-spin" />
                              ) : (
                                buttonText
                              )}
                            </SqButton>
                          </span>
                        </SqTooltipTrigger>
                        <SqTooltipContent
                          portal={true}
                          align="end"
                          side="top"
                          className="max-w-[280px]"
                        >
                          {submitButtonTooltipMessage}
                        </SqTooltipContent>
                      </SqTooltip>
                    ) : (
                      <span className="inline-flex">
                        <SqButton
                          type="submit"
                          variant="default"
                          size="md"
                          className="font-medium"
                          disabled={
                            !isFormValid() ||
                            isSubmitting ||
                            isCreativeOverviewSelectionIncomplete ||
                            isAdMetadataSelectionIncomplete ||
                            !hasEditFormChanges
                          }
                        >
                          {isSubmitting ? (
                            <LuLoaderCircle className="h-4 w-4 animate-spin" />
                          ) : (
                            buttonText
                          )}
                        </SqButton>
                      </span>
                    )}
                  </div>
                </div>

                {/* ── Advanced Settings Panel ── */}
                {openSection === "advanced" && (
                  <div className="w-full overflow-hidden border-t border-[#D0D5DD99] pl-[13px] pr-[14px] pt-[13px] pb-[14px] flex flex-col gap-[20px]">
                    {/* Label Source */}
                    <div>
                      <div className="flex items-center gap-[6px] mb-[10px]">
                        <span className="text-[12px] font-medium text-brand-primary-800">Label source</span>
                        <SqTooltip>
                          <SqTooltipTrigger type="button">
                            <RiInformationLine size={14} className="text-[#CCD1DE]" />
                          </SqTooltipTrigger>
                          <SqTooltipContent portal={true} align="start" side="bottom" className="max-w-[264px]">
                            Labels are created based on the context you share with AI. Choose <span className="font-bold">Auto</span> to let AI decide what to use, or pick Creative or Ad metadata to control exactly what information is used for grouping.
                          </SqTooltipContent>
                        </SqTooltip>
                      </div>
                      <SegmentedToggle
                        value={labelSource}
                        onChange={(v) => setLabelSource(v as "auto" | "creative" | "ad_metadata")}
                        options={[
                          { value: "auto", label: "Auto" },
                          { value: "creative", label: "Creative" },
                          { value: "ad_metadata", label: "Ad Metadata" },
                        ]}
                      />
                    </div>

                    {/* Creative overview (shown when label source = creative): Auto = LLM picks overviews, Manual = user picks */}
                    {labelSource === "creative" && (
                      <div>
                        <div className="flex items-center gap-[6px] mb-[10px]">
                          <span className="text-[12px] font-medium text-brand-primary-800">Creative overview</span>
                          <SqTooltip>
                            <SqTooltipTrigger type="button">
                              <RiInformationLine size={14} className="text-[#CCD1DE]" />
                            </SqTooltipTrigger>
                            <SqTooltipContent portal={true} align="start" side="bottom" className="max-w-[264px]">
                              Creatives include visuals, audio, hooks, and more. Use <span className="font-bold">Manual</span> to choose the context, or <span className="font-bold">Auto</span> to let AI pick the best overview for your label prompt.
                            </SqTooltipContent>
                          </SqTooltip>
                        </div>
                        <div className="pb-1">
                          <div className="flex items-center gap-[11px]">
                            <SegmentedToggle
                              value={overviewSelectionType}
                              onChange={(v) => setOverviewSelectionType(v as "auto" | "manual")}
                              options={[
                                { value: "auto", label: "Auto" },
                                { value: "manual", label: "Manual" },
                              ]}
                            />
                            <div className="overflow-x-auto scrollbar-hide flex items-center gap-[6px] flex-nowrap min-w-0">
                              <SqTooltip>
                                <SqTooltipTrigger asChild>
                                  <span className="inline-flex">
                                    <SqButton
                                      variant="outline"
                                      size="md"
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (overviewSelectionType === "manual") {
                                          setIsCreateOverviewOpen(true);
                                        }
                                      }}
                                      disabled={overviewSelectionType !== "manual"}
                                      aria-label="Add overview"
                                      className="h-[32px] w-[32px]"
                                    >
                                      <RiAddLargeLine size={14} className="shrink-0 text-brand-primary-400" />
                                    </SqButton>
                                  </span>
                                </SqTooltipTrigger>
                                <SqTooltipContent portal={true} align="start" side="bottom" className="max-w-[264px]">
                                  Create a new overview if you don't see the right fit.
                                </SqTooltipContent>
                              </SqTooltip>
                              {filteredOverviews.map((ov) => (
                                <CheckboxPill
                                  key={ov.id}
                                  checked={selectedOverviewIds.includes(ov.id)}
                                  onChange={(c) =>
                                    setSelectedOverviewIds((prev) =>
                                      c ? [...prev, ov.id] : prev.filter((x) => x !== ov.id),
                                    )
                                  }
                                  label={ov.title}
                                  icon={
                                    videoChecked && imageChecked ? (
                                      <>
                                        {ov.creative_type === "video" ? (
                                          <RiMovieLine size={16} className="text-brand-primary-400" />
                                        ) : (
                                          <RiImage2Line size={16} className="text-brand-primary-400" />
                                        )}
                                      </>

                                    ) : undefined
                                  }
                                  disabled={overviewSelectionType !== "manual"}
                                />
                              ))}
                              {filteredOverviews.length === 0 && overviewSelectionType === "manual" && (
                                <span className="text-[12px] text-brand-primary-400 shrink-0">No overviews available for the selected media type</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Ad metadata (shown when label source = ad_metadata): Auto = LLM picks fields, Manual = user picks */}
                    {labelSource === "ad_metadata" && (
                      <div>
                        <div className="flex items-center gap-[6px] mb-[10px]">
                          <span className="text-[12px] font-medium text-brand-primary-800">Ad metadata fields</span>
                          <SqTooltip>
                            <SqTooltipTrigger type="button">
                              <RiInformationLine size={14} className="text-[#CCD1DE]" />
                            </SqTooltipTrigger>
                            <SqTooltipContent portal={true} align="start" side="bottom" className="max-w-[264px]">
                              Ads include metadata like ad name, campaign name, ad set name, headline, body, and more. Use <span className="font-bold">Manual</span> to choose which fields to include, or <span className="font-bold">Auto</span> to let AI pick the most relevant overview for your label prompt.
                            </SqTooltipContent>
                          </SqTooltip>
                        </div>
                        <div className="pb-1">
                          <div className="flex items-center gap-[11px]">
                            <SegmentedToggle
                              value={metadataSelectionType}
                              onChange={(v) => setMetadataSelectionType(v as "auto" | "manual")}
                              options={[
                                { value: "auto", label: "Auto" },
                                { value: "manual", label: "Manual" },
                              ]}
                            />
                            <div className="overflow-x-auto scrollbar-hide flex items-center gap-[6px] flex-nowrap">
                              {([
                                { key: "ad_name", label: "Ad name" },
                                { key: "adset_name", label: "Adset name" },
                                { key: "campaign_name", label: "Campaign name" },
                                { key: "ad_body", label: "Ad body" },
                                { key: "ad_heading", label: "Ad heading" },
                              ] as const).map((field) => (
                                <CheckboxPill
                                  key={field.key}
                                  checked={selectedMetadata.includes(field.key)}
                                  onChange={(c) =>
                                    setSelectedMetadata((prev) =>
                                      c ? [...prev, field.key] : prev.filter((m) => m !== field.key),
                                    )
                                  }
                                  label={field.label}
                                  disabled={metadataSelectionType !== "manual"}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Label Type */}
                    <div>
                      <div className="flex items-center gap-[6px] mb-[10px]">
                        <span className="text-[12px] font-medium text-brand-primary-800">Label Type</span>
                        <SqTooltip>
                          <SqTooltipTrigger type="button">
                            <RiInformationLine size={14} className="text-[#CCD1DE]" />
                          </SqTooltipTrigger>
                          <SqTooltipContent portal={true} align="start" side="bottom" className="max-w-[264px]">
                            <span className="font-bold">Unique</span> assigns each creative to only one group. <span className="font-bold">Multiple</span> lets the same creative appear in more than one group when it fits.
                          </SqTooltipContent>
                        </SqTooltip>
                      </div>
                      <SegmentedToggle
                        value={labelType}
                        onChange={(v) => setLabelType(v as "unique" | "multiple")}
                        options={[
                          { value: "unique", label: "Unique" },
                          {
                            value: "multiple",
                            label: "Multiple",
                            disabled: videoChecked && imageChecked,
                            tooltip:
                              videoChecked && imageChecked
                                ? 'Multi-tagging works on one asset type at a time. Switch to "Image" or "Video" to enable it.'
                                : undefined,
                          },
                        ]}
                      />
                    </div>
                  </div>
                )}
              </form>
            )}
          </SqTooltipProvider>
        </DialogContent>
      </Dialog>

      {/* ── Gallery Preview ── */}
      <CreativeGalleryPreview
        open={galleryOpen}
        onOpenChange={handleGalleryOpenChange}
        creatives={galleryCreatives}
        currentGroup={galleryGroupName}
        groupType="text_ai_label"
        labelConfigId={id}
        accountId={accountId}
        labelView={labelType === "multiple" ? "multiple" : undefined}
        enableSearch={galleryCreatives.length > 4}
        loading={galleryLoading}
        totalCreatives={galleryTotal > 0 ? galleryTotal : undefined}
        onLoadMore={galleryTotal > 0 ? handleGalleryLoadMore : undefined}
      />

      {/* ── Unsaved changes confirmation ── */}
      <Dialog open={unsavedChangesDialogOpen} onOpenChange={setUnsavedChangesDialogOpen}>
        <DialogContent hideCloseButton className="flex w-[483px] max-w-[512px] flex-col items-start gap-4 rounded-[12px] bg-[#FFF] p-3 shadow-sm overflow-hidden border-0">
          <DialogHeader className="flex flex-col items-start gap-0 p-0 w-full space-y-0">
            <DialogTitle className="text-[#021859] font-medium text-[14px] leading-[28px]">
              Update label changes?
            </DialogTitle>
            <p className="text-[12px] font-normal leading-[20px] text-[rgba(2,24,89,0.80)]">
              You changed the label. Do you want to update it before continuing?
            </p>
          </DialogHeader>
          <div className="flex items-center justify-between gap-3 w-full">
            <div>

              <SqButton
                type="button"
                variant="outline"
                size="md"
                className="font-medium"
                onClick={() => setUnsavedChangesDialogOpen(false)}
              >
                Keep editing
              </SqButton>
            </div>
            <div className="flex items-center gap-2">
              <SqButton
                type="button"
                variant="outline"
                size="md"
                className="font-medium"
                onClick={() => {
                  setUnsavedChangesDialogOpen(false);
                  setOpen(false);
                  onClose();
                }}
              >
                Discard changes
              </SqButton>
              <SqButton
                type="button"
                variant="default"
                size="md"
                className="font-medium"
                onClick={() => {
                  setUnsavedChangesDialogOpen(false);
                  formRef.current?.requestSubmit();
                }}
              >
                Update & Continue
              </SqButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Create Overview Dialog ── */}
      <CreateEditDialog
        isOpen={isCreateOverviewOpen}
        onOpenChange={setIsCreateOverviewOpen}
        onSubmit={(t, d, creativeType) => handleCreateOverview(t, d, creativeType)}
        title=""
        description=""
        showMediaType
      />
    </>
  );
};
